import React, { FC, useCallback, useEffect, useState } from 'react'
import CopyToClipBoard from 'react-copy-to-clipboard'
import QRCode from 'qrcode.react'
import styles from 'styles/components/card.module.scss'
import { Card, CardInner } from 'components/Card'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { useQueryStoreName } from 'hooks/store'
import { FlexRow } from 'components/Flex'
import { BlueButton, GreyButton } from 'components/Button'
import { Spacer } from 'components/Utility'
import { useQueryDynamicLink } from 'hooks/dynamiclink'
import { BasicModal } from 'components/Modal'
import useModal from 'hooks/form/useModal'
import { Tooltip, TooltipWrapper } from 'components/Tooltip'
import { isPcDevice } from 'lib/judge'
import { SubDescription } from 'components/Typography'
import { useTooltip } from 'hooks/form'

const DeepLinkCard: FC = () => {
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeName = useQueryStoreName(storeRef)
  const dynamicLink = useQueryDynamicLink(storeRef)

  const [open, closeModal, openModal] = useModal()
  const [src, setSrc] = useState<string>('')
  const { tooltip, openTooltip } = useTooltip()

  // canvasに描画したデータをimgタグのsrcにセット
  useEffect(() => {
    const canvas = document.getElementById('qrcode') as HTMLCanvasElement
    if (!canvas) return
    setSrc(canvas.toDataURL('image/png', 0.85))
  }, [dynamicLink])

  // canvasに描画したデータをa.downloadにセット（iOS Chromeだとダウンロードできない）
  const downloadQrCode = useCallback(() => {
    const canvas = document.getElementById('qrcode') as HTMLCanvasElement
    if (!canvas) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png', 0.85)
    a.download = storeName + '.png'
    a.click()
  }, [storeName])

  // リンクとハッシュタグの間の改行ができない
  const shareTwitter = useCallback(() => {
    const encodedUrl = encodeURI(dynamicLink)
    const encodedTags = encodeURI('パンの取り置き,サクリ')
    window.open(
      'https://twitter.com/intent/tweet?text=' +
        'パンの取り置き予約始めました！%23sacri をダウンロードすると、' +
        '食べたいパンが取り置きできるタイミングをお知らせ！レジに並ばずに商品を受け取ることができます。%0a%0a' +
        '🍞アプリのダウンロードはこちらから🍞%0a' +
        encodedUrl +
        '&hashtags=' +
        encodedTags
    )
  }, [dynamicLink])

  const shareFacebook = useCallback(() => {
    const encodedUrl = encodeURI(dynamicLink)
    // PCであれば別タブで開く
    if (isPcDevice()) {
      window.open('https://www.facebook.com/share.php?u=' + encodedUrl)
    } else {
      // Facebookアプリで起動する
      let fbApiUrl = 'fbapi://dialog/share?app_id=968129357255397&version=2.0&method_args='
      const methodArgs = {
        name: null,
        description: null,
        link: dynamicLink,
        quote: `${storeName} | sacri`,
        hashtag: null,
        dataFailuresFatal: false,
      }
      fbApiUrl += encodeURIComponent(JSON.stringify(methodArgs))
      window.location.href = fbApiUrl
      // アプリ未インストールの場合
      if (/^.*\/account\/settings\/deep_link$/.test(window.location.href)) {
        window.open('https://www.facebook.com/share.php?u=' + encodedUrl)
      }
    }
  }, [dynamicLink])

  return (
    <Card>
      <CardInner>
        <h3 className={styles.deeplink__title}>
          {storeName}様<br />
          専用URL
        </h3>
        <Spacer size={16} />
        <FlexRow justifyContent={'center'}>
          <QRCode id={'qrcode'} size={160} value={dynamicLink} style={{ display: 'none' }} />
          <img src={src} alt={storeName} width={160} height={160} />
        </FlexRow>
        <Spacer size={12} />
        <a className={styles.deeplink__url} href={dynamicLink} target={'_blank'} rel={'noopener nofollow'}>
          {dynamicLink}
        </a>
        <Spacer size={20} />
        <FlexRow itemNum={'two'} justifyContent={'center'}>
          <BlueButton label={'QR画像をダウンロード'} onClick={openModal} size={'xsmall'} type={'outlined'} />
          <Spacer size={12} />
          <CopyToClipBoard text={dynamicLink}>
            <BlueButton label={'URLをコピー'} onClick={openTooltip} size={'xsmall'} type={'outlined'} />
          </CopyToClipBoard>
          <TooltipWrapper open={tooltip}>
            <Tooltip direction={'top'} label={'Copied!'} />
          </TooltipWrapper>
        </FlexRow>
        <Spacer size={12} />
        <FlexRow itemNum={'two'} justifyContent={'center'}>
          <BlueButton label={'Twitterでシェア'} onClick={shareTwitter} size={'xsmall'} type={'outlined'} />
          <Spacer size={12} />
          <BlueButton label={'facebookでシェア'} onClick={shareFacebook} size={'xsmall'} type={'outlined'} />
        </FlexRow>
        <Spacer size={12} />
        <FlexRow justifyContent={'center'}>
          <SubDescription
            description={'※画像をダウンロードできない場合、QRコード画像を長押しや右クリックして保存してください'}
          />
        </FlexRow>
      </CardInner>
      <BasicModal open={open} closeModal={closeModal}>
        <h3 className={styles.deeplink__modal__title}>画像ダウンロード</h3>
        <Spacer size={16} />
        <p>"{storeName}.png"をダウンロードしますか？</p>
        <Spacer size={20} />
        <FlexRow justifyContent={'center'}>
          <GreyButton label={'キャンセル'} onClick={closeModal} size={'small'} type={'outlined'} />
          <Spacer size={16} />
          <BlueButton label={'ダウンロード'} onClick={downloadQrCode} size={'small'} type={'main'} />
        </FlexRow>
      </BasicModal>
    </Card>
  )
}

export default DeepLinkCard
